import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { AuthBindings, Authenticated, Refine } from "@refinedev/core";
import RefineAuthWrapper from "./components/RefineAuthWrapper";
import LoginPage from "./components/LoginPage";
import { createSafeDataProvider } from "./providers/safe-data-provider";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbarProvider } from "@refinedev/kbar";
import { getToken } from "./utils/auth-storage";
import { authMonitor } from "./utils/auth-monitor";
import { AuthProvider, useAuth } from "./contexts/auth-context";
import { GeocodingProvider } from "./providers/GeocodingProvider";
import { createLogger } from "./utils/logger";

import {
  notificationProvider,
  RefineSnackbarProvider,
  ThemedLayoutV2,
} from "@refinedev/mui";
import { Title } from "./components/title/Title";
import { Header as AppHeader } from "./components/header";

import GlobalStyles from "@mui/material/GlobalStyles";
import CssBaseline from "@mui/material/CssBaseline";
import routerBindings from "@refinedev/react-router-v6";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { CustomSiderStyles, CustomSider } from "./components/sider";
import { LayoutDashboard, Users, Calendar, Tag, Building, Settings, MapPin, UserCircle, Locate, HardDrive, History, Radio } from "lucide-react";
import { OnboardingTrigger } from "./components/onboarding/OnboardingTrigger";
import DashboardHome from "./pages/dashboard";
import InviteRegistration from "./pages/invite/InviteRegistration";
import InviteSuccess from "./pages/invite/InviteSuccess";
import { UserList } from "./pages/user/UserList";
import { UserShow } from "./pages/user/UserShow";
import { UserEdit } from "./pages/user/UserEdit";
import { UserCreate } from "./pages/user/UserCreate";
import {
  EventList,
  EventCreate,
  EventEdit,
  EventShow,
} from "./pages/event/index";
import {
  PromocodeList,
  PromocodeShow,
  PromocodeEdit,
  PromocodeCreate,
} from "./pages/promocode/index";
import {
  PremiumDeviceList,
  PremiumDeviceShow,
  PremiumDeviceEdit,
  PremiumDeviceCreate,
} from "./pages/premiumDevices/index";
import {
  DiscountList,
  DiscountShow,
  DiscountEdit,
  DiscountCreate,
} from "./pages/discounts/index";
import { postLogin } from "./api/auth.api";
import { useTranslation } from "react-i18next";
import { API_CONFIG } from "./config/api";
import { HistoryList } from "./pages/history/HistoryList";
import { AlertHistoryShow } from "./pages/alert-history/AlertHistoryShow";
import { ForbiddenZoneShow } from "./pages/history/ForbiddenZoneShow";
import { CompanyShow } from "./pages/entreprises/CompanyShow";
import { CompanyEdit } from "./pages/entreprises/CompanyEdit";
import { CompanyCreate } from "./pages/entreprises/CompanyCreate";
import { SubGroupMembers } from "./pages/entreprises/SubGroupMembers";
import { OTAList } from "./pages/ota/OTAList";
import { OTACreate } from "./pages/ota/OTACreate";
import { LocationList } from "./pages/location/LocationList";
import { MonitoringList } from "./pages/monitoring/MonitoringList";
import { MonitoringAlertShow } from "./pages/monitoring/MonitoringAlertShow";
import { SharedPlacesList, SharedPlacesCreate, SharedPlacesEdit } from "./pages/sharedPlaces";
// ForbiddenZoneHistoryList removed — now part of unified HistoryList
import { CircularProgress } from "@mui/material";
import RoleGuard from "./components/RoleGuard";
import { filterResourcesByRole, UserRole } from "./utils/role-permissions";
import PricingPage from "./pages/pricing/PricingPage";
import SuccessPage from "./pages/pricing/SuccessPage";
import CancelPage from "./pages/pricing/CancelPage";
import ErrorPage from "./pages/pricing/ErrorPage";
import ProfilePage from "./pages/profile/ProfilePage";
import ProfileEditPage from "./pages/profile/ProfileEditPage";
import { CompanyRedirect } from "./components/CompanyRedirect";
import { VigilaneLink } from "./pages/vigilane";
import { JourneyShow } from "./pages/journeys";
import { ProviderList, ProviderCreate, ProviderEdit } from "./pages/telesurveilleur";
import { NotificationPermissionBanner } from "./components/NotificationPermissionBanner";
import { MonitoringIcon } from "./components/MonitoringIcon";
import { useNotifications } from "./hooks/useNotifications";
import { alertSoundManager } from "./utils/notifications/AlertSoundManager";

// Component to listen for Service Worker messages, navigate, and manage alert sound
const NavigationListener: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-unlock audio on first user gesture
  React.useEffect(() => {
    alertSoundManager.initUserGesture();
  }, []);

  // Listen for SW messages: NAVIGATE, ALERT_SOUND_PLAY, ALERT_SOUND_STOP
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data) return;

      switch (event.data.type) {
        case 'NAVIGATE':
          if (event.data.url) navigate(event.data.url);
          break;
        case 'ALERT_SOUND_PLAY':
          alertSoundManager.play(event.data.alertId, event.data.url);
          break;
        case 'ALERT_SOUND_STOP':
          alertSoundManager.stop();
          break;
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  // Auto-stop sound when navigating to monitoring pages
  React.useEffect(() => {
    if (location.pathname.startsWith('/monitoring')) {
      alertSoundManager.stop();
    }
  }, [location.pathname]);

  return null;
};

// Banner displayed when an emergency alert sound is playing (or pending autoplay unlock).
// Uses position:fixed + CSS custom property to push the MUI layout (sidebar + content) down.
const BANNER_HEIGHT = 48;
const BANNER_TRANSITION_MS = 350;

const AlertSoundBanner: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [, forceUpdate] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);

  React.useEffect(() => {
    return alertSoundManager.subscribe(() => {
      forceUpdate((n) => n + 1);
    });
  }, []);

  const pending = alertSoundManager.getPendingAlert();
  const playing = alertSoundManager.isPlaying();
  const shouldShow = playing || !!pending;

  // Toggle CSS variable on <html> to offset the fixed MUI sidebar + body
  React.useEffect(() => {
    if (shouldShow) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setExpanded(true));
      });
    } else {
      setExpanded(false);
      const timer = setTimeout(() => setMounted(false), BANNER_TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [shouldShow]);

  // Sync the CSS variable with expanded state
  React.useEffect(() => {
    const val = expanded ? `${BANNER_HEIGHT}px` : '0px';
    document.documentElement.style.setProperty('--alert-banner-h', val);
  }, [expanded]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      document.documentElement.style.setProperty('--alert-banner-h', '0px');
    };
  }, []);

  if (!mounted) return null;

  const handleViewAlert = () => {
    const url = pending?.url || '/monitoring';
    alertSoundManager.stop();
    navigate(url);
  };

  const handleDismiss = () => {
    alertSoundManager.stop();
  };

  return (
    <>
      <style>{`
        @keyframes alertPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        body {
          padding-top: var(--alert-banner-h, 0px);
          transition: padding-top ${BANNER_TRANSITION_MS}ms ease-in-out;
        }
        .MuiDrawer-paper.MuiDrawer-paperAnchorLeft {
          top: var(--alert-banner-h, 0px) !important;
          height: calc(100vh - var(--alert-banner-h, 0px)) !important;
          transition: top ${BANNER_TRANSITION_MS}ms ease-in-out, height ${BANNER_TRANSITION_MS}ms ease-in-out !important;
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: BANNER_HEIGHT,
          zIndex: 9999,
          transform: expanded ? 'translateY(0)' : 'translateY(-100%)',
          transition: `transform ${BANNER_TRANSITION_MS}ms ease-in-out`,
        }}
      >
        <div
          style={{
            background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 50%, #d32f2f 100%)',
            color: '#fff',
            padding: '0 24px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
            animation: 'alertPulse 1.5s ease-in-out infinite',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22 }} role="img" aria-label="alert">&#128680;</span>
            <div>
              <strong style={{ fontSize: 14 }}>{t('alertBanner.title')}</strong>
              {!playing && pending && (
                <div style={{ fontSize: 11, opacity: 0.9, marginTop: 1 }}>
                  {t('alertBanner.autoplayBlocked')}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={handleViewAlert}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff',
                padding: '6px 16px',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {t('alertBanner.viewAlert')}
            </button>
            <button
              onClick={handleDismiss}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 20,
                padding: '4px 8px',
                lineHeight: 1,
              }}
              aria-label={t('alertBanner.close')}
            >
              &times;
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const authLogger = createLogger('AUTH');

// Main App component that uses the auth context
const AppContent = () => {
  const API_URL = API_CONFIG.baseURL;
  const { t } = useTranslation();
  const { isAuthenticated, loginWithUserData, logout, user, isLoading } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);
  const [loginInProgress, setLoginInProgress] = useState(false);
  const { subscribe } = useNotifications();

  // Subscribe to push notifications when user is authenticated
  React.useEffect(() => {
    const actualPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';

    if (actualPermission === 'granted' && user?.id) {
      subscribe(user.id.toString()).catch(() => {
        // Silently ignore subscription errors
      });
    }
  }, [user?.id, subscribe]);

  // Gestionnaire de login sécurisé amélioré
  const handleLogin = React.useCallback(async (credentials: {
    email: string;
    password: string;
  }) => {
    setLoginInProgress(true);
    setAuthError(null);

    try {
      const { accessToken } = await postLogin(credentials);

      // Use the auth context login method that returns user data
      const userData = await loginWithUserData(accessToken);

      if (userData && (userData.role === "ADMIN" || userData.role === "SAFEE_STAFF" || userData.role === "MODERATOR" || userData.role === "MODERATOR_FREE" || userData.role === "SUB_MODERATOR" || userData.role === "SAFEE_MODERATOR" || userData.role === "GUEST" || userData.role === "VIGILANE" || userData.role === "OPERATOR")) {

        // Start auth monitor after successful login
        authMonitor.start();

        setLoginInProgress(false);
        return true;
      } else {
        authLogger.warn(`User has unauthorized role: ${userData?.role}`);
        // Ne pas appeler logout() ici pour éviter de vider le localStorage et causer un rechargement
        // Simplement retirer le token invalide
        localStorage.removeItem("accessToken");
        setAuthError(t('auth.errors.unauthorized_role', { role: userData?.role || 'undefined' }));
        setLoginInProgress(false);
        return false;
      }
    } catch (error) {
      authLogger.error("Login error:", error);
      setAuthError(t('auth.errors.login_error'));
      setLoginInProgress(false);
      return false;
    }
  }, [loginWithUserData, t]);

  // Simplified auth provider for Refine that uses our context
  const authProvider: AuthBindings = {
    login: async ({ email, password }) => {
      const success = await handleLogin({ email, password });

      if (success) {
        return { success: true, redirectTo: "/users" };
      }

      return {
        success: false,
        error: {
          message: authError || t('auth.errors.login_failed'),
          name: "LoginError",
        },
      };
    },

    check: async () => {
      const token = getToken();
      if (!token) {
        return { authenticated: false, redirectTo: "/login" };
      }

      // Use the auth context state instead of making another API call
      if (isAuthenticated && user) {
        return { authenticated: true };
      }

      return { authenticated: false, redirectTo: "/login" };
    },

    logout: async () => {
      authMonitor.stop();
      logout();
      return { success: true, redirectTo: "/login" };
    },

    onError: async (error) => {
      const status = error?.response?.status;

      // Only logout on 401 (token expired/invalid)
      // 403 means "forbidden" (authenticated but not authorized) — not a session issue
      if (status === 401) {
        logout();
        return {
          logout: true,
          redirectTo: "/login",
          error: {
            message: t('auth.errors.session_expired'),
            name: "Session Expired",
          },
        };
      }

      return { error };
    },

    getIdentity: async () => {
      // Use cached user data from context
      if (user) {
        return {
          id: user.id.toString(),
          name: user.fullname || user.email,
          role: user.role,
        };
      }
      return null;
    },
  };

  // Page de connexion personnalisée - mémorisée pour éviter les re-renders
  const Login = React.useMemo(() => {
    return () => (
      <LoginPage
        onSubmit={handleLogin}
        error={authError}
        loading={loginInProgress}
      />
    );
  }, [handleLogin, authError, loginInProgress]);

  // Utiliser le fournisseur de données sécurisé
  const dataProviderInstance = createSafeDataProvider(API_URL);

  // Define all resources
  const allResources = [
    // Catégorie Principal
    {
      name: "dashboard",
      list: DashboardHome,
      meta: {
        icon: <LayoutDashboard size={22} />,
        label: t('dashboard.title'),
      },
    },
    {
      name: "monitoring",
      list: "/monitoring",
      show: "/monitoring/:id",
      meta: {
        icon: <MonitoringIcon />,
        label: t('monitoring.title'),
      },
    },
    {
      name: "history",
      list: "/history",
      show: "/history/:id",
      meta: {
        icon: <History size={22} />,
        label: t('history.titles'),
      },
    },
    {
      name: "location",
      list: "/location",
      meta: {
        label: t("location.title"),
        icon: <Locate size={22} />,
      },
    },
    {
      name: "places",
      list: "/shared-places",
      create: "/shared-places/create",
      edit: "/shared-places/edit/:id",
      show: "/shared-places/show/:id",
      meta: {
        icon: <MapPin size={22} />,
        label: t("sharedPlaces.title"),
        route: "/shared-places",
      },
    },
    // Catégorie Gestion
    {
      name: "users",
      list: "/users",
      show: "/users/:id",
      create: "/users/create",
      edit: "/users/edit/:id",
      meta: {
        label: t("users.title"),
        icon: <Users size={22} />,
      },
    },
    {
      name: "company",
      list: "/company",
      show: "/company/:id",
      create: "/company/create",
      edit: "/company/edit/:id",
      meta: {
        label: t("company.title"),
        icon: <Building size={22} />,
      },
    },
    {
      name: "safe-events",
      list: "/safe-events",
      create: "/safe-events/create",
      show: "/safe-events/:id",
      edit: "/safe-events/edit/:id",
      meta: {
        label: t("events.title"),
        icon: <Calendar size={22} />,
      },
    },
    {
      name: "promo-codes",
      list: "/promo-codes",
      create: "/promo-codes/create",
      edit: "/promo-codes/edit/:id",
      show: "/promo-codes/show/:id",
      meta: {
        label: t("promocodes.title"),
        icon: <Tag size={22} />,
      },
    },
    {
      name: "premium-devices",
      list: "/premium-devices",
      create: "/premium-devices/create",
      edit: "/premium-devices/edit/:id",
      show: "/premium-devices/show/:id",
      meta: {
        label: "Premium Devices",
        icon: <HardDrive size={22} />,
      },
    },
    {
      name: "ota",
      list: "/ota",
      create: "/ota/create",
      meta: {
        icon: <Settings size={22} />,
        label: t('ota.title'),
      },
    },
    {
      name: "telesurveilleur",
      list: "/telesurveilleur",
      create: "/telesurveilleur/create",
      edit: "/telesurveilleur/edit/:id",
      meta: {
        label: "Télésurveilleur",
        icon: <Radio size={22} />,
      },
    },
    {
      name: "places",
      list: "/shared-places",
      create: "/shared-places/create",
      edit: "/shared-places/edit/:id",
      show: "/shared-places/show/:id",
      meta: {
        icon: <MapPin size={22} />,
        label: t("places.title"),
        route: "/shared-places",
      },
    },
    // Menu Profil (caché du menu, accessible via le footer profil)
    {
      name: "profile",
      list: "/profile",
      meta: {
        icon: <UserCircle size={22} />,
        label: "Profil",
        hide: true,
      },
    },
  ];

  // Filter resources based on user role (for navigation menu visibility)
  // Note: Route-level protection is handled by RoleGuard components
  let filteredResources = user?.role
    ? filterResourcesByRole(allResources, user.role as UserRole)
    : allResources;

  // For OPERATOR: put monitoring first in the sidebar
  if (user?.role === 'OPERATOR') {
    const monitoringIndex = filteredResources.findIndex(r => r.name === 'monitoring');
    if (monitoringIndex > 0) {
      const [monitoring] = filteredResources.splice(monitoringIndex, 1);
      filteredResources = [monitoring, ...filteredResources];
    }
  }

  // Configurer le fournisseur i18n
  const { i18n } = useTranslation();
  const i18nProvider = {
    translate: (key: string, params: string) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </div>
    );
  }


  return (
    <BrowserRouter>
      <NavigationListener />
      <AlertSoundBanner />
      <RefineKbarProvider>
        <ColorModeContextProvider>
          <CssBaseline />
          <GlobalStyles styles={{
            html: { WebkitFontSmoothing: "auto" },
            // Désactiver le rendu HDR sur les images (photos iPhone notamment)
            'img, .MuiAvatar-img': {
              filter: 'saturate(1)',
            },
          }} />
          <RefineSnackbarProvider>
            <GeocodingProvider>
              <DevtoolsProvider>
                {/* Dev Onboarding Trigger */}
                <OnboardingTrigger />
                <Routes>
                <Route path="login" element={<Login />} />
                {/* Public invite-based registration page (no auth) */}
                <Route path="invite/:companyId/:inviteCode" element={<InviteRegistration />} />
                <Route path="invite/:companyId" element={<InviteRegistration />} />
                <Route path="invite/success" element={<InviteSuccess />} />
                <Route
                  path="*"
                  element={
                    <RefineAuthWrapper>
                      <Refine
                        notificationProvider={notificationProvider}
                        routerProvider={routerBindings}
                        authProvider={authProvider}
                        i18nProvider={i18nProvider}
                        dataProvider={dataProviderInstance}
                        options={{
                          syncWithLocation: true,
                          warnWhenUnsavedChanges: false,
                          disableTelemetry: true,
                        }}
                        resources={filteredResources}
                      >
                        <Routes>
                          <Route
                            element={
                              <Authenticated
                                key="dashboard"
                                redirectOnFail="/login"
                                v3LegacyAuthProviderCompatible
                              >
                                <ThemedLayoutV2
                                  Title={Title}
                                  Sider={CustomSider}
                                  Header={AppHeader}
                                >
                                  <CustomSiderStyles />
                                  {/* Notification Permission Banner - only shown when authenticated */}
                                  <NotificationPermissionBanner />
                                  <Outlet />
                                </ThemedLayoutV2>
                              </Authenticated>
                            }
                          >
                            <Route index element={user?.role === 'OPERATOR' ? <Navigate to="/monitoring" replace /> : <DashboardHome />} />
                            <Route path="dashboard" element={<DashboardHome />} />
                            <Route path="users">
                              <Route index element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "GUEST", "VIGILANE", "OPERATOR", "OT"]}>
                                  <UserList />
                                </RoleGuard>
                              } />
                              <Route path=":id" element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "GUEST", "VIGILANE", "OPERATOR", "OT"]}>
                                  <UserShow />
                                </RoleGuard>
                              } />
                              <Route path="edit/:id" element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "OPERATOR", "VIGILANE", "OT"]}>
                                  <UserEdit />
                                </RoleGuard>
                              } />
                              <Route path="create" element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE"]}>
                                  <UserCreate />
                                </RoleGuard>
                              } />
                            </Route>
                            <Route path="safe-events">
                              <Route index element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "SAFEE_MODERATOR"]}>
                                  <EventList />
                                </RoleGuard>
                              } />
                              <Route path=":id" element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "SAFEE_MODERATOR"]}>
                                  <EventShow />
                                </RoleGuard>
                              } />
                              <Route path="edit/:id" element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "SAFEE_MODERATOR"]}>
                                  <EventEdit />
                                </RoleGuard>
                              } />
                              <Route path="create" element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF"]}>
                                  <EventCreate />
                                </RoleGuard>
                              } />
                            </Route>
                              <Route path="promo-codes">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <PromocodeList />
                                  </RoleGuard>
                                } />
                                <Route path="create" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <PromocodeCreate />
                                  </RoleGuard>
                                } />
                                <Route path="edit/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <PromocodeEdit />
                                  </RoleGuard>
                                } />
                                <Route path="show/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <PromocodeShow />
                                  </RoleGuard>
                                } />
                              </Route>
                              <Route path="premium-devices">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF"]}>
                                    <PremiumDeviceList />
                                  </RoleGuard>
                                } />
                                <Route path="create" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <PremiumDeviceCreate />
                                  </RoleGuard>
                                } />
                                <Route path="edit/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF"]}>
                                    <PremiumDeviceEdit />
                                  </RoleGuard>
                                } />
                                <Route path="show/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF"]}>
                                    <PremiumDeviceShow />
                                  </RoleGuard>
                                } />
                              </Route>
                              <Route path="discounts">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <DiscountList />
                                  </RoleGuard>
                                } />
                                <Route path="show/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <DiscountShow />
                                  </RoleGuard>
                                } />
                                <Route path="edit/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <DiscountEdit />
                                  </RoleGuard>
                                } />
                                <Route path="create" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <DiscountCreate />
                                  </RoleGuard>
                                } />
                              </Route>
                              <Route path="history">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "VIGILANE", "OPERATOR", "OT"]}>
                                    <HistoryList />
                                  </RoleGuard>
                                } />
                                <Route path="alert/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "VIGILANE", "OPERATOR", "OT"]}>
                                    <AlertHistoryShow />
                                  </RoleGuard>
                                } />
                                <Route path="journey/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "OPERATOR"]}>
                                    <JourneyShow />
                                  </RoleGuard>
                                } />
                                <Route path="forbidden-zone/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "VIGILANE", "OPERATOR", "OT"]}>
                                    <ForbiddenZoneShow />
                                  </RoleGuard>
                                } />
                              </Route>
                              {/* Redirects from old URLs */}
                              <Route path="alert-history" element={<Navigate to="/history" replace />} />
                              <Route path="alert-history/:id" element={<Navigate to="/history" replace />} />
                              <Route path="company">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "GUEST", "VIGILANE", "OPERATOR"]}>
                                    <CompanyRedirect />
                                  </RoleGuard>
                                } />
                                <Route path=":id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "GUEST", "VIGILANE", "OPERATOR"]}>
                                    <CompanyShow />
                                  </RoleGuard>
                                } />
                                <Route path="edit/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE"]}>
                                    <CompanyEdit />
                                  </RoleGuard>
                                } />
                                <Route path="create" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "GUEST"]}>
                                    <CompanyCreate />
                                  </RoleGuard>
                                } />
                                <Route path="sub-groups/:id/members" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "VIGILANE", "OPERATOR"]}>
                                    <SubGroupMembers />
                                  </RoleGuard>
                                } />
                              </Route>
                              <Route path="ota">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <OTAList />
                                  </RoleGuard>
                                } />
                                <Route path="create" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <OTACreate />
                                  </RoleGuard>
                                } />
                              </Route>
                              <Route path="telesurveilleur">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <ProviderList />
                                  </RoleGuard>
                                } />
                                <Route path="create" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <ProviderCreate />
                                  </RoleGuard>
                                } />
                                <Route path="edit/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN"]}>
                                    <ProviderEdit />
                                  </RoleGuard>
                                } />
                              </Route>
                              <Route path="location">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR"]}>
                                    <LocationList />
                                  </RoleGuard>
                                } />
                              </Route>
                              {/* Redirects from old journey URLs */}
                              <Route path="journeys" element={<Navigate to="/history" replace />} />
                              <Route path="journeys/:id" element={<Navigate to="/history" replace />} />
                              <Route path="monitoring">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "SAFEE_MODERATOR", "VIGILANE", "OPERATOR"]}>
                                    <MonitoringList />
                                  </RoleGuard>
                                } />
                                <Route path=":id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "SAFEE_MODERATOR", "VIGILANE", "OPERATOR"]}>
                                    <MonitoringAlertShow />
                                  </RoleGuard>
                                } />
                              </Route>
                              <Route path="shared-places">
                                <Route index element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "VIGILANE", "OPERATOR"]}>
                                    <SharedPlacesList />
                                  </RoleGuard>
                                } />
                                <Route path="create" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "VIGILANE", "OPERATOR"]}>
                                    <SharedPlacesCreate />
                                  </RoleGuard>
                                } />
                                <Route path="edit/:id" element={
                                  <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "VIGILANE", "OPERATOR"]}>
                                    <SharedPlacesEdit />
                                  </RoleGuard>
                                } />
                              </Route>
                              {/* Redirect from old forbidden-zone-history URL */}
                              <Route path="forbidden-zone-history" element={<Navigate to="/history" replace />} />
                              <Route path="pricing" element={
                                <RoleGuard allowedRoles={["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SUB_MODERATOR", "SAFEE_MODERATOR", "GUEST"]}>
                                  <PricingPage />
                                </RoleGuard>
                              } />
                              <Route path="profile" element={<ProfilePage />} />
                              <Route path="profile/edit" element={<ProfileEditPage />} />
                              <Route path="vigilane-link" element={
                                <RoleGuard allowedRoles={["VIGILANE"]}>
                                  <VigilaneLink />
                                </RoleGuard>
                              } />
                              <Route path="success" element={<SuccessPage />} />
                              <Route path="cancel" element={<CancelPage />} />
                              <Route path="error" element={<ErrorPage />} />
                          </Route>
                        </Routes>
                      </Refine>
                    </RefineAuthWrapper>
                  }
                />
              </Routes>
              <DevtoolsPanel />
            </DevtoolsProvider>
            </GeocodingProvider>
          </RefineSnackbarProvider>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
};

// Wrapper component with AuthProvider
function App() {
  // const { i18n } = useTranslation();

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
